import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Database, X, Search, Upload, Download, RotateCcw, Edit, Trash2, Plus, Image as ImageIcon, FileSpreadsheet, Loader2, ListX } from 'lucide-react';
import { useAppContext, trLower } from '../context/AppContext';
import {
  extractCodesFromRows,
  findCatalogItemsMissingFromCodes,
  findCodeUsageInProposals,
  catalogCodeKey,
  type ProposalUsageRef,
  isReviewTrustworthy,
  MIN_MATCH_RATIO,
  type CatalogPruneReview,
} from '../utils/catalogDiff';
import { findExactColorItem } from '../utils/colorCatalog';

export default function CatalogModal() {
  const {
    userRole,
    catalog,
    isCatalogModalOpen,
    setIsCatalogModalOpen,
    catalogSearch,
    setCatalogSearch,
    selectedCatalogCategory,
    setSelectedCatalogCategory,
    catalogActiveTab,
    setCatalogActiveTab,
    isEditingCatalogItem,
    setIsEditingCatalogItem,
    setEditingItemCode,
    catalogForm,
    setCatalogForm,
    compatibilityRules,
    selectedMatrixModel,
    setSelectedMatrixModel,
    selectedMatrixMaterial,
    setSelectedMatrixMaterial,
    newMatrixModelName,
    setNewMatrixModelName,
    newMatrixMaterialName,
    setNewMatrixMaterialName,
    newMatrixColorName,
    setNewMatrixColorName,
    catalogImportRef,
    excelImportRef,
    isUploadingExcel,
    handleDownloadExcelTemplate,
    saveCompatibilityRules,
    deleteCatalogItemsByCode,
    handleSaveCatalogItem,
    handleEditCatalogItemClick,
    handleDeleteCatalogItem,
    handleResetCatalogToDefault,
    handleResetMatrixToDefault,
    handleExportCatalog,
    handleExportCatalogExcel,
    handleImportCatalog,
    handleImportExcel,
    handleCatalogItemImageChange,
    handleCatalogBrowserImageUpload,
    handleMatrixColorImageUpload,
  } = useAppContext();

  const [selectedItemCodes, setSelectedItemCodes] = useState<string[]>([]);

  // "Excel'de olmayanları bul" — tek seferlik temizlik yardımcısı. Dosyadan yalnızca
  // kod listesi okunur; hiçbir şey silinmez, önce inceleme paneli açılır.
  const pruneFileRef = useRef<HTMLInputElement>(null);
  const [pruneReview, setPruneReview] = useState<CatalogPruneReview | null>(null);
  const [pruneFileName, setPruneFileName] = useState('');
  const [isPruning, setIsPruning] = useState(false);
  // Silmeden önce teklif kontrolü: null = henüz yapılmadı (silme kapalı).
  const [pruneUsage, setPruneUsage] = useState<{ byCode: Map<string, ProposalUsageRef[]>; scanned: number } | null>(null);
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);

  // Clear selections when category, search query, or active tab changes
  useEffect(() => {
    setSelectedItemCodes([]);
  }, [selectedCatalogCategory, catalogSearch, catalogActiveTab]);

  const filteredItems = catalog
    .filter(item => {
      if (catalogSearch.trim()) {
        const q = trLower(catalogSearch);
        return trLower(item.code).includes(q) || trLower(item.name).includes(q) || trLower(item.category).includes(q);
      }
      return item.category === selectedCatalogCategory;
    });

  const isAllSelected = filteredItems.length > 0 && filteredItems.every(item => selectedItemCodes.includes(item.code));

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedItemCodes(prev => {
        const union = new Set([...prev, ...filteredItems.map(item => item.code)]);
        return Array.from(union);
      });
    } else {
      setSelectedItemCodes(prev => prev.filter(code => !filteredItems.some(item => item.code === code)));
    }
  };

  const handleToggleSelectItem = (code: string) => {
    setSelectedItemCodes(prev => 
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`${selectedItemCodes.length} adet ürünü katalogdan silmek istediğinize emin misiniz?`)) {
      await deleteCatalogItemsByCode(selectedItemCodes);
      setSelectedItemCodes([]);
    }
  };

  const handlePruneFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      let codes: string[] | null = null;
      for (const sheetName of wb.SheetNames) {
        const rows: any[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '' });
        codes = extractCodesFromRows(rows);
        if (codes) break;
      }
      if (!codes) {
        alert('Dosyada "Kod / Ürün Kodu" başlıklı bir sütun bulunamadı. Güvenlik için hiçbir şey seçilmedi.');
        return;
      }
      setPruneFileName(file.name);
      setPruneUsage(null);
      setPruneReview(findCatalogItemsMissingFromCodes(catalog, codes));
    } catch (err) {
      alert('Excel dosyası okunurken hata oluştu: ' + (err as Error).message);
    }
  };

  const downloadPruneBackup = (items: typeof catalog) => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(items, null, 2));
    const a = document.createElement('a');
    a.setAttribute('href', dataStr);
    a.setAttribute('download', `intema_katalog_silinenler_${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Salt okunur: tüm teklifleri okuyup silinecek kodları taşıyanları bulur (yalnızca admin okuyabilir).
  const handleCheckUsage = async () => {
    if (!pruneReview || !db) return;
    setIsCheckingUsage(true);
    try {
      const snap = await getDocs(collection(db, 'proposals'));
      const docs: Array<Record<string, any>> = [];
      snap.forEach(d => docs.push({ ...d.data(), id: d.id }));
      setPruneUsage({ byCode: findCodeUsageInProposals(docs, pruneReview.missing.map(i => i.code)), scanned: docs.length });
    } catch (err) {
      alert('Teklifler okunamadı (admin yetkisi gerekir): ' + (err as Error).message + '\n\nKontrol yapılamadığı için silme kapalı kalır.');
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const handlePruneConfirm = async () => {
    if (!pruneReview || !pruneUsage || !isReviewTrustworthy(pruneReview) || pruneReview.missing.length === 0) return;
    const items = pruneReview.missing;
    const usedCount = items.filter(i => pruneUsage.byCode.has(catalogCodeKey(i.code))).length;
    const usedWarn = usedCount > 0 ? `\n\n⚠️ Bunlardan ${usedCount} ürün kayıtlı tekliflerde geçiyor (teklifler kendi kopyasını taşır, ama emin olun).` : '';
    if (!window.confirm(`${items.length} adet ürün katalogdan KALICI olarak silinecek.${usedWarn}\n\nÖnce silinecek ürünlerin JSON yedeği indirilecek (JSON Yükle ile geri alınabilir). Devam edilsin mi?`)) return;
    setIsPruning(true);
    try {
      downloadPruneBackup(items);
      await deleteCatalogItemsByCode(items.map(i => i.code));
      setPruneReview(null);
      alert(`✅ ${items.length} ürün katalogdan silindi.`);
    } finally {
      setIsPruning(false);
    }
  };

  if (!isCatalogModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm no-print">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full h-[85vh] flex flex-col overflow-hidden border border-stone-200 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="bg-stone-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Database className="w-5 h-5 text-brand-400" />
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider">Ürün Kataloğu Yönetim Paneli</h2>
              <p className="text-[10px] text-stone-400 normal-case">Katalog öğelerini arayın, ekleyin, düzenleyin ve silin.</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="bg-emerald-500/20 text-emerald-300 text-[9px] font-bold px-2 py-0.5 rounded-full border border-emerald-500/30">
              {catalog.length} Toplam Ürün
            </span>
            <button 
              onClick={() => setIsCatalogModalOpen(false)}
              className="text-stone-400 hover:text-white p-1 rounded-full hover:bg-stone-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Sub-Header Tabs */}
        <div className="bg-stone-800 px-6 py-2 flex space-x-4 border-t border-stone-700 text-xs font-semibold text-stone-300">
          <button
            type="button"
            onClick={() => setCatalogActiveTab('products')}
            className={`py-1.5 px-3 rounded transition-all ${
              catalogActiveTab === 'products' ? 'bg-stone-700 text-white shadow-sm' : 'hover:text-white'
            }`}
          >
            Ürün Kataloğu
          </button>
          <button
            type="button"
            onClick={() => {
              setCatalogActiveTab('matrix');
              const models = Object.keys(compatibilityRules);
              if (models.length > 0 && !selectedMatrixModel) {
                setSelectedMatrixModel(models[0]);
                const materials = Object.keys(compatibilityRules[models[0]] || {});
                if (materials.length > 0) {
                  setSelectedMatrixMaterial(materials[0]);
                }
              }
            }}
            className={`py-1.5 px-3 rounded transition-all ${
              catalogActiveTab === 'matrix' ? 'bg-stone-700 text-white shadow-sm' : 'hover:text-white'
            }`}
          >
            Model, Malzeme & Renk Matrisi
          </button>
        </div>

        {/* Modal Body */}
        {catalogActiveTab === 'products' ? (
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-stone-50/50">
          
            {/* Left Pane: Product list & search */}
            <div className="flex-1 p-5 flex flex-col overflow-hidden border-r border-stone-200/80">
              
              {/* Search & Actions Bar */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4 items-center justify-between">
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <div className="relative w-full sm:max-w-xs">
                    <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-stone-400" />
                    <input 
                      type="text"
                      placeholder="Kod veya isim ile ara..."
                      value={catalogSearch}
                      onChange={e => {
                        setCatalogSearch(e.target.value);
                        setSelectedItemCodes([]);
                      }}
                      className="w-full pl-9 pr-4 py-1.5 text-xs rounded-md border border-stone-200 focus:outline-none focus:border-brand-500 bg-white placeholder:text-stone-400 font-medium"
                    />
                  </div>
                  {selectedItemCodes.length > 0 && (
                    <button
                      type="button"
                      onClick={handleBulkDelete}
                      className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-bold flex items-center space-x-1 transition-all shadow-xs animate-in fade-in"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Toplu Sil ({selectedItemCodes.length})</span>
                    </button>
                  )}
                </div>
                
                {/* Backup & Reset buttons (Visible only to Admin) */}
                {userRole === 'admin' && (
                  <div className="flex items-center space-x-2 text-[10px]">
                    <input 
                      type="file" 
                      ref={catalogImportRef}
                      onChange={handleImportCatalog}
                      accept=".json"
                      className="hidden" 
                    />
                    <input 
                      type="file" 
                      ref={excelImportRef}
                      onChange={handleImportExcel}
                      accept=".xlsx,.xls,.csv"
                      className="hidden" 
                    />
                    <button
                      type="button"
                      onClick={() => catalogImportRef.current?.click()}
                      className="px-2 py-1 border border-stone-200 rounded bg-white text-stone-600 hover:bg-stone-50 flex items-center space-x-1 transition-colors font-semibold shadow-xs"
                      title="JSON yedekten yükle"
                    >
                      <Upload className="w-2.5 h-2.5 text-stone-400" />
                      <span>JSON Yükle</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCatalog}
                      className="px-2 py-1 border border-stone-200 rounded bg-white text-stone-600 hover:bg-stone-50 flex items-center space-x-1 transition-colors font-semibold shadow-xs"
                      title="JSON olarak indir"
                    >
                      <Download className="w-2.5 h-2.5 text-stone-400" />
                      <span>JSON İndir</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => excelImportRef.current?.click()}
                      className="px-2 py-1 border border-stone-200 rounded bg-white text-stone-600 hover:bg-stone-50 flex items-center space-x-1 transition-colors font-semibold shadow-xs"
                      title="Excel dosyasından yükle"
                    >
                      <FileSpreadsheet className="w-2.5 h-2.5 text-stone-400" />
                      <span>Excel Yükle</span>
                    </button>
                    <input
                      type="file"
                      ref={pruneFileRef}
                      onChange={handlePruneFilePicked}
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => pruneFileRef.current?.click()}
                      className="px-2 py-1 border border-amber-200 rounded bg-white text-amber-700 hover:bg-amber-50 flex items-center space-x-1 transition-colors font-semibold shadow-xs"
                      title="Seçilen Excel'de kodu bulunmayan katalog ürünlerini listele (silmeden önce onay ister)"
                    >
                      <ListX className="w-2.5 h-2.5" />
                      <span>Excel'de Olmayanları Bul</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCatalogExcel}
                      className="px-2 py-1 border border-stone-200 rounded bg-white text-stone-600 hover:bg-stone-50 flex items-center space-x-1 transition-colors font-semibold shadow-xs"
                      title="Kataloğu Excel olarak indir"
                    >
                      <Download className="w-2.5 h-2.5 text-stone-400" />
                      <span>Excel İndir</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleResetCatalogToDefault}
                      className="px-2 py-1 border border-red-200 rounded bg-white text-red-600 hover:bg-red-50 flex items-center space-x-1 transition-colors font-semibold shadow-xs"
                      title="Kataloğu varsayılana sıfırla"
                    >
                      <RotateCcw className="w-2.5 h-2.5" />
                      <span>Kataloğu Sıfırla</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Category Filters Tab Bar */}
              <div className="flex flex-wrap bg-stone-200/60 p-0.5 rounded-lg border border-stone-200/30 text-[10px] gap-0.5 mb-4">
                {['Aksesuar', 'Aydınlatma', 'Kulp', 'Tezgâh', 'Renk', 'Cihaz', 'Eviye', 'Armatür'].map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCatalogCategory(cat)}
                    className={`px-2.5 py-1 font-bold rounded transition-all ${
                      selectedCatalogCategory === cat 
                        ? 'bg-white text-brand-700 shadow-sm' 
                        : 'text-stone-500 hover:text-stone-800'
                    }`}
                  >
                    {cat === 'Renk' ? 'Kapak Rengi' : cat}
                  </button>
                ))}
              </div>

              {/* Scrollable list */}
              <div className="flex-1 overflow-y-auto border border-stone-200 rounded-lg bg-white shadow-inner">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-stone-50 text-[10px] font-bold text-stone-400 border-b border-stone-100 uppercase tracking-wider">
                      <th className="py-2 px-3 w-8">
                        <input 
                          type="checkbox" 
                          onChange={handleSelectAll} 
                          checked={isAllSelected}
                          className="rounded border-stone-300 text-[#945f46] focus:ring-[#945f46]"
                        />
                      </th>
                      <th className="py-2 px-3 w-16">Görsel</th>
                      <th className="py-2 px-3">Kod</th>
                      <th className="py-2 px-3">Kategori</th>
                      <th className="py-2 px-3">Ürün Adı</th>
                      <th className="py-2 px-3 w-24 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
                    {filteredItems.map((item, idx) => {
                      return (
                        <tr key={item.code + idx} className="hover:bg-stone-50/50 transition-colors">
                          <td className="py-2 px-3 w-8">
                            <input 
                              type="checkbox" 
                              checked={selectedItemCodes.includes(item.code)}
                              onChange={() => handleToggleSelectItem(item.code)}
                              className="rounded border-stone-300 text-[#945f46] focus:ring-[#945f46]"
                            />
                          </td>
                          <td className="py-2 px-3">
                            <div className="relative group/img">
                              <label
                                htmlFor={`catalog-direct-img-${idx}-${item.code}`}
                                className="w-9 h-9 rounded border border-stone-200 bg-stone-50 flex items-center justify-center cursor-pointer hover:border-brand-500 hover:bg-brand-50 transition-all overflow-hidden block relative shadow-2xs"
                                title="Ürün görselini tek tıkla yüklemek / değiştirmek için tıklayın"
                              >
                                {item.image ? (
                                  <img src={item.image} alt={item.code} className="w-full h-full object-contain" />
                                ) : (
                                  <ImageIcon className="w-4 h-4 text-stone-400 group-hover/img:text-brand-600 mx-auto" />
                                )}
                                <div className="absolute inset-0 bg-black/50 text-white text-[8px] font-bold opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity rounded">
                                  Yükle
                                </div>
                              </label>
                              <input
                                type="file"
                                id={`catalog-direct-img-${idx}-${item.code}`}
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleCatalogBrowserImageUpload(item.code, file);
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </div>
                          </td>
                          <td className="py-2 px-3 font-bold text-stone-900 font-mono tracking-tight text-[11px]">{item.code}</td>
                          <td className="py-2 px-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-stone-100 text-stone-700 border border-stone-200/60">
                              {item.category === 'Renk' ? 'Kapak Rengi' : (item.category || '-')}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-stone-600 truncate max-w-[240px]" title={item.name}>{item.name}</td>
                          <td className="py-2 px-3 text-right">
                              <div className="flex items-center justify-end space-x-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleEditCatalogItemClick(item)}
                                  className="text-brand-600 hover:text-brand-700 p-1 hover:bg-stone-100 rounded transition-colors"
                                  title="Düzenle"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCatalogItem(item.code)}
                                  className="text-red-500 hover:text-red-650 p-1 hover:bg-stone-100 rounded transition-colors"
                                  title="Sil"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right Pane: Form */}
            <div className="w-full md:w-80 bg-stone-50/80 p-5 flex flex-col overflow-y-auto border-l border-stone-200/50">
              <div className="bg-white p-4 rounded-xl border border-stone-200/80 shadow-sm space-y-4">
                <div className="flex items-center space-x-2 text-stone-800 font-bold border-b border-stone-100 pb-2">
                  {isEditingCatalogItem ? (
                    <>
                      <Edit className="w-4 h-4 text-brand-600" />
                      <span className="text-xs uppercase tracking-wider">Ürün Düzenle</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 text-brand-600" />
                      <span className="text-xs uppercase tracking-wider">Yeni Ürün Ekle</span>
                    </>
                  )}
                </div>

                <form onSubmit={handleSaveCatalogItem} className="space-y-3.5">
                  {!isEditingCatalogItem && (
                    <div className="bg-stone-50 p-2.5 rounded-lg border border-stone-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-[9px] uppercase font-bold text-stone-400 block">Excel Dosyasından Toplu Yükle</label>
                        <button
                          type="button"
                          onClick={handleDownloadExcelTemplate}
                          className="text-[9px] text-brand-600 hover:text-brand-700 underline font-bold flex items-center space-x-1 transition-colors"
                          title="Örnek Excel yükleme şablonunu indirin"
                        >
                          <Download className="w-2.5 h-2.5" />
                          <span>Şablon İndir</span>
                        </button>
                      </div>
                      <button
                        type="button"
                        disabled={isUploadingExcel}
                        onClick={() => excelImportRef.current?.click()}
                        className="w-full px-3 py-1.5 text-xs font-bold rounded border border-brand-200 bg-white text-brand-700 hover:bg-stone-50 transition-colors shadow-sm flex items-center justify-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Excel dosyasından toplu ürün yükle"
                      >
                        {isUploadingExcel ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 text-brand-600 animate-spin" />
                            <span>İşleniyor &amp; Yükleniyor...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-3.5 h-3.5 text-brand-600" />
                            <span>Excel / CSV Yükle</span>
                          </>
                        )}
                      </button>
                      <p className="text-[8px] text-stone-400 italic">Kategori, Kod, İsim (Görsel isteğe bağlı) sütunlarını içerir.</p>
                    </div>
                  )}

                  <div>
                    <label className="text-[9px] uppercase font-bold text-stone-400 block">Kategori</label>
                    <select
                      value={catalogForm.category}
                      onChange={e => setCatalogForm({ ...catalogForm, category: e.target.value })}
                      className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-md border border-stone-200 bg-stone-50 focus:outline-none focus:border-brand-500 font-medium text-stone-700"
                    >
                      <option value="Aksesuar">Aksesuar</option>
                      <option value="Aydınlatma">Aydınlatma</option>
                      <option value="Kulp">Kulp</option>
                      <option value="Tezgâh">Tezgâh</option>
                      <option value="Renk">Kapak Rengi</option>
                      <option value="Cihaz">Cihaz</option>
                      <option value="Eviye">Eviye</option>
                      <option value="Armatür">Armatür</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[9px] uppercase font-bold text-stone-400 block">Ürün Kodu</label>
                    <input 
                      type="text"
                      placeholder="Örn: A2SP45"
                      value={catalogForm.code}
                      onChange={e => setCatalogForm({ ...catalogForm, code: e.target.value })}
                      className="w-full mt-1 px-3 py-1.5 text-xs rounded-md border border-stone-200 focus:outline-none focus:border-brand-500 bg-white placeholder:text-stone-305 font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[9px] uppercase font-bold text-stone-400 block">Ürün Adı</label>
                    <input 
                      type="text"
                      placeholder="Örn: Ev Eviyesi veya Kulp"
                      value={catalogForm.name}
                      onChange={e => setCatalogForm({ ...catalogForm, name: e.target.value })}
                      className="w-full mt-1 px-3 py-1.5 text-xs rounded-md border border-stone-200 focus:outline-none focus:border-brand-500 bg-white placeholder:text-stone-305 font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1.5">Ürün Görseli</label>
                    <input 
                      type="file" 
                      id="catalog-item-image-file"
                      accept="image/*"
                      onChange={handleCatalogItemImageChange}
                      className="hidden" 
                    />
                    <div 
                      onClick={() => document.getElementById('catalog-item-image-file')?.click()}
                      className="mt-1 flex justify-center px-6 pt-4 pb-4 border-2 border-stone-200 border-dashed rounded-lg bg-stone-50/50 cursor-pointer hover:border-brand-400 hover:bg-stone-50 transition-colors"
                    >
                      {catalogForm.image ? (
                        <div className="relative">
                          <img 
                            src={catalogForm.image} 
                            alt="Önizleme" 
                            className="h-20 object-contain rounded border bg-white"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=G%C3%B6rsel+Bulunamad%C4%B1';
                            }}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCatalogForm(prev => ({ ...prev, image: null, imageRemoved: true }));
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors shadow-sm"
                            title="Görseli Kaldır"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1 text-center">
                          <ImageIcon className="mx-auto h-7 w-7 text-stone-300" />
                          <div className="text-[10px] text-stone-600 font-semibold text-brand-650 hover:text-brand-700">
                            Bilgisayardan Dosya Seç
                          </div>
                          <p className="text-[8px] text-stone-400">PNG, JPG, JPEG (Max 5MB)</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="text-[9px] font-bold text-stone-400 uppercase block mb-1">veya Görsel Bağlantısı (URL)</label>
                      <input 
                        type="text"
                        placeholder="https://ornek.com/gorsel.jpg"
                        value={catalogForm.image && !catalogForm.image.startsWith('data:') ? catalogForm.image : ''}
                        onChange={(e) => {
                          const url = e.target.value.trim();
                          setCatalogForm(prev => ({
                            ...prev,
                            image: url || null,
                            imageRemoved: !url
                          }));
                        }}
                        className="w-full px-3 py-1.5 text-xs rounded-md border border-stone-200 focus:outline-none focus:border-brand-500 bg-white placeholder:text-stone-300 font-medium"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex items-center gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-brand-600 hover:bg-brand-700 text-white py-2 text-xs font-semibold rounded-md transition-all shadow-sm flex items-center justify-center space-x-1"
                    >
                      <span>{isEditingCatalogItem ? 'Güncelle' : 'Ekle'}</span>
                    </button>
                    
                    {isEditingCatalogItem && (
                      <button
                        type="button"
                        onClick={() => {
                          setCatalogForm({
                            category: selectedCatalogCategory,
                            code: '',
                            name: '',
                            image: null
                          });
                          setIsEditingCatalogItem(false);
                          setEditingItemCode(null);
                        }}
                        className="px-3 bg-stone-200 hover:bg-stone-300 text-stone-700 py-2 text-xs font-semibold rounded-md transition-all"
                      >
                        İptal
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-stone-100/50">
            {/* Column 1: Models */}
            <div className="w-full md:w-1/3 p-4 flex flex-col border-r border-stone-200/80 bg-white">
              <div className="flex items-center justify-between border-b border-stone-150 pb-2 mb-3">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">1. Modeller</span>
                {userRole === 'admin' && (
                  <button
                    type="button"
                    onClick={handleResetMatrixToDefault}
                    className="px-2 py-0.5 border border-red-200 rounded bg-white text-red-600 hover:bg-red-50 flex items-center space-x-0.5 transition-colors font-bold text-[8px] shadow-xs"
                    title="Matrisi varsayılana sıfırla"
                  >
                    <RotateCcw className="w-2 h-2" />
                    <span>Matrisi Sıfırla</span>
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                {Object.keys(compatibilityRules).map(modelName => (
                  <div 
                    key={modelName}
                    onClick={() => {
                      setSelectedMatrixModel(modelName);
                      const mats = Object.keys(compatibilityRules[modelName] || {});
                      setSelectedMatrixMaterial(mats.length > 0 ? mats[0] : '');
                    }}
                    className={`flex items-center justify-between p-2.5 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                      selectedMatrixModel === modelName 
                        ? 'bg-brand-50 border-brand-200 text-brand-700 shadow-sm' 
                        : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'
                    }`}
                  >
                    <span>{modelName}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`${modelName} modelini ve buna bağlı tüm malzeme/renk eşleşmelerini silmek istediğinize emin misiniz?`)) {
                          const updated = { ...compatibilityRules };
                          delete updated[modelName];
                          saveCompatibilityRules(updated);
                          const keys = Object.keys(updated);
                          setSelectedMatrixModel(keys.length > 0 ? keys[0] : '');
                          setSelectedMatrixMaterial(keys.length > 0 && Object.keys(updated[keys[0]]).length > 0 ? Object.keys(updated[keys[0]])[0] : '');
                        }
                      }}
                      className="text-stone-400 hover:text-red-650 p-0.5 rounded transition-colors"
                      title="Modeli Sil"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-stone-150 mt-3 space-y-2">
                <input 
                  type="text"
                  placeholder="Yeni model adı..."
                  value={newMatrixModelName}
                  onChange={e => setNewMatrixModelName(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs rounded border border-stone-250 bg-white focus:outline-none focus:border-brand-500 font-semibold"
                />
                <button
                  type="button"
                  onClick={() => {
                    const name = newMatrixModelName.trim().toUpperCase();
                    if (!name) return;
                    if (compatibilityRules[name]) {
                      alert('Bu model zaten mevcut.');
                      return;
                    }
                    const updated = { ...compatibilityRules, [name]: {} };
                    saveCompatibilityRules(updated);
                    setSelectedMatrixModel(name);
                    setSelectedMatrixMaterial('');
                    setNewMatrixModelName('');
                  }}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold py-1.5 rounded transition-colors flex items-center justify-center space-x-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Model Ekle</span>
                </button>
              </div>
            </div>

            {/* Column 2: Materials */}
            <div className="w-full md:w-1/3 p-4 flex flex-col border-r border-stone-200/80 bg-stone-50/50">
              <div className="flex items-center justify-between border-b border-stone-150 pb-2 mb-3">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">2. Malzemeler ({selectedMatrixModel || 'Seçilmedi'})</span>
              </div>
              {selectedMatrixModel ? (
                <>
                  <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                    {Object.keys(compatibilityRules[selectedMatrixModel] || {}).map(materialName => (
                      <div 
                        key={materialName}
                        onClick={() => setSelectedMatrixMaterial(materialName)}
                        className={`flex items-center justify-between p-2.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                          selectedMatrixMaterial === materialName 
                            ? 'bg-brand-50 border-brand-200 text-brand-700 shadow-sm' 
                            : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'
                        }`}
                      >
                        <span>{materialName}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`${selectedMatrixModel} altındaki ${materialName} malzemesini ve renklerini silmek istediğinize emin misiniz?`)) {
                              const updated = { ...compatibilityRules };
                              delete updated[selectedMatrixModel][materialName];
                              saveCompatibilityRules(updated);
                              const keys = Object.keys(updated[selectedMatrixModel]);
                              setSelectedMatrixMaterial(keys.length > 0 ? keys[0] : '');
                            }
                          }}
                          className="text-stone-400 hover:text-red-650 p-0.5 rounded transition-colors"
                          title="Malzemeyi Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-stone-150 mt-3 space-y-2">
                    <input 
                      type="text"
                      placeholder="Yeni malzeme adı..."
                      value={newMatrixMaterialName}
                      onChange={e => setNewMatrixMaterialName(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs rounded border border-stone-250 bg-white focus:outline-none focus:border-brand-500 font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const name = newMatrixMaterialName.trim();
                        if (!name) return;
                        if (compatibilityRules[selectedMatrixModel]?.[name]) {
                          alert('Bu malzeme zaten bu model altında mevcut.');
                          return;
                        }
                        const updated = {
                          ...compatibilityRules,
                          [selectedMatrixModel]: {
                            ...(compatibilityRules[selectedMatrixModel] || {}),
                            [name]: []
                          }
                        };
                        saveCompatibilityRules(updated);
                        setSelectedMatrixMaterial(name);
                        setNewMatrixMaterialName('');
                      }}
                      className="w-full bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold py-1.5 rounded transition-colors flex items-center justify-center space-x-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Malzeme Ekle</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-stone-400 italic">
                  Lütfen sol taraftan bir model seçiniz.
                </div>
              )}
            </div>

            {/* Column 3: Colors */}
            <div className="w-full md:w-1/3 p-4 flex flex-col bg-white">
              <div className="flex items-center justify-between border-b border-stone-150 pb-2 mb-3">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">3. Renkler ({selectedMatrixMaterial || 'Seçilmedi'})</span>
              </div>
              {selectedMatrixModel && selectedMatrixMaterial ? (
                <>
                  <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                    {(compatibilityRules[selectedMatrixModel]?.[selectedMatrixMaterial] || []).map(colorName => (
                      <div 
                        key={colorName}
                        className="flex items-center justify-between p-2.5 rounded-lg border border-stone-150/70 bg-stone-50/30 text-xs font-medium text-stone-700"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0">
                          <label
                            htmlFor={`matrix-color-img-${colorName}`}
                            title="Renk görselini yüklemek / değiştirmek için tıklayın"
                            className="relative group/img w-9 h-9 shrink-0 rounded border border-stone-200 bg-white flex items-center justify-center overflow-hidden cursor-pointer hover:border-brand-500 transition-colors"
                          >
                            {findExactColorItem(colorName, catalog)?.image ? (
                              <img src={findExactColorItem(colorName, catalog)!.image!} alt={colorName} className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon className="w-4 h-4 text-stone-400 group-hover/img:text-brand-600" />
                            )}
                            <span className="absolute inset-0 bg-black/50 text-white text-[8px] font-bold opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity">
                              Yükle
                            </span>
                          </label>
                          <input
                            type="file"
                            id={`matrix-color-img-${colorName}`}
                            accept="image/*"
                            className="hidden"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) handleMatrixColorImageUpload(colorName, file);
                              e.target.value = '';
                            }}
                          />
                          <span className="truncate">{colorName}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const colors = compatibilityRules[selectedMatrixModel][selectedMatrixMaterial];
                            const updatedColors = colors.filter(c => c !== colorName);
                            const updated = {
                              ...compatibilityRules,
                              [selectedMatrixModel]: {
                                ...compatibilityRules[selectedMatrixModel],
                                [selectedMatrixMaterial]: updatedColors
                              }
                            };
                            saveCompatibilityRules(updated);
                          }}
                          className="text-stone-400 hover:text-red-655 p-0.5 rounded transition-colors"
                          title="Rengi Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-stone-150 mt-3 space-y-2">
                    <input 
                      type="text"
                      placeholder="Yeni renk adı..."
                      value={newMatrixColorName}
                      onChange={e => setNewMatrixColorName(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs rounded border border-stone-250 bg-white focus:outline-none focus:border-brand-500 font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const name = newMatrixColorName.trim();
                        if (!name) return;
                        const colors = compatibilityRules[selectedMatrixModel][selectedMatrixMaterial] || [];
                        if (colors.includes(name)) {
                          alert('Bu renk zaten bu eşleşme altında mevcut.');
                          return;
                        }
                        const updated = {
                          ...compatibilityRules,
                          [selectedMatrixModel]: {
                            ...compatibilityRules[selectedMatrixModel],
                            [selectedMatrixMaterial]: [...colors, name]
                          }
                        };
                        saveCompatibilityRules(updated);
                        setNewMatrixColorName('');
                      }}
                      className="w-full bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold py-1.5 rounded transition-colors flex items-center justify-center space-x-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Renk Ekle</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-stone-400 italic">
                  Lütfen model ve malzeme seçiniz.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Footer */}
        <div className="bg-stone-50 border-t border-stone-200 px-6 py-3 flex justify-between items-center text-[10px] text-stone-400">
          <span>* Sistem ürünleri varsayılan olarak korunur; yalnızca eklediğiniz özel ürünleri düzenleyebilir veya silebilirsiniz.</span>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setIsCatalogModalOpen(false)}
              className="bg-stone-200 hover:bg-stone-300 text-stone-700 px-4 py-1.5 font-bold rounded-md transition-colors"
            >
              Kapat
            </button>
          </div>
        </div>

        {pruneReview && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-stone-900/60">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full flex flex-col border border-stone-200">
              <div className="px-5 py-3 border-b border-stone-200 flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-bold text-stone-800">Excel'de olmayan ürünler</h3>
                  <p className="text-[11px] text-stone-500">
                    {pruneFileName}: {pruneReview.fileCodeCount} kod, bunların {pruneReview.matchedCount}'i katalogda var.
                    Katalogda {catalog.length} ürün var; <strong>{pruneReview.missing.length}</strong> ürün dosyada yok.
                  </p>
                </div>
                <button type="button" onClick={() => setPruneReview(null)} className="text-stone-400 hover:text-stone-700 p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {!isReviewTrustworthy(pruneReview) ? (
                <div className="p-5 text-xs text-red-700 bg-red-50">
                  Dosyadaki kodların %{Math.round(MIN_MATCH_RATIO * 100)}'inden azı katalogla eşleşiyor. Yanlış dosya seçilmiş olabilir; güvenlik için silme kapalı.
                </div>
              ) : pruneReview.missing.length === 0 ? (
                <div className="p-5 text-xs text-emerald-700 bg-emerald-50">Katalogdaki tüm ürünler dosyada var. Silinecek bir şey yok.</div>
              ) : (
                <div className="overflow-y-auto flex-1">
                  {pruneUsage && (() => {
                    const used = pruneReview.missing.filter(i => pruneUsage.byCode.has(catalogCodeKey(i.code))).length;
                    return (
                      <div className={`px-5 py-2 text-[11px] font-semibold ${used > 0 ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>
                        {pruneUsage.scanned} teklif tarandı: {used > 0 ? `${used} ürün kayıtlı tekliflerde geçiyor (fareyle üzerine gelince teklif adları görünür).` : 'silinecek ürünlerin hiçbiri hiçbir teklifte geçmiyor.'}
                      </div>
                    );
                  })()}
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-stone-50 text-stone-500 text-left">
                      <tr><th className="px-4 py-1.5">Kategori</th><th className="px-2 py-1.5">Kod</th><th className="px-2 py-1.5">Ad</th><th className="px-2 py-1.5">Görsel</th><th className="px-2 py-1.5">Teklifte</th></tr>
                    </thead>
                    <tbody>
                      {pruneReview.missing.map(i => (
                        <tr key={i.code} className="border-t border-stone-100">
                          <td className="px-4 py-1">{i.category}</td>
                          <td className="px-2 py-1 font-mono">{i.code}</td>
                          <td className="px-2 py-1">{i.name}</td>
                          <td className="px-2 py-1">{!i.image ? '—' : i.image.startsWith('data:') ? 'yüklenmiş' : i.image.startsWith('http') ? 'bulut' : 'dosya'}</td>
                          <td className="px-2 py-1" title={(pruneUsage?.byCode.get(catalogCodeKey(i.code)) ?? []).map(r => `${r.clientName || r.id}${r.status ? ' (' + r.status + ')' : ''}`).join(', ')}>
                            {!pruneUsage ? '?' : (pruneUsage.byCode.get(catalogCodeKey(i.code))?.length ?? 0) || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="px-5 py-3 border-t border-stone-200 flex items-center justify-end space-x-2 text-xs">
                <button type="button" onClick={() => setPruneReview(null)} className="px-3 py-1.5 rounded-md bg-stone-200 hover:bg-stone-300 text-stone-700 font-bold">
                  Vazgeç
                </button>
                {isReviewTrustworthy(pruneReview) && pruneReview.missing.length > 0 && (
                  <>
                    <button type="button" onClick={() => downloadPruneBackup(pruneReview.missing)} className="px-3 py-1.5 rounded-md border border-stone-200 bg-white hover:bg-stone-50 text-stone-700 font-bold">
                      Listeyi JSON İndir
                    </button>
                    {!pruneUsage && (
                      <button type="button" onClick={handleCheckUsage} disabled={isCheckingUsage} className="px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold flex items-center space-x-1">
                        {isCheckingUsage && <Loader2 className="w-3 h-3 animate-spin" />}
                        <span>Tekliflerde Kullanımı Kontrol Et</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handlePruneConfirm}
                      disabled={isPruning || !pruneUsage}
                      title={!pruneUsage ? 'Önce tekliflerde kullanım kontrolünü yapın' : undefined}
                      className="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold flex items-center space-x-1"
                    >
                      {isPruning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      <span>{pruneReview.missing.length} Ürünü Sil</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
